import { DateTime } from "luxon";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import type { DatePickerMode, NewCalendarFilters, NewCalendarViewMode } from "../types";

interface NewCalendarUIState {
  // Core view state
  viewMode: NewCalendarViewMode;
  currentDate: DateTime;

  // Multi-selection state
  selectedAppointmentIds: string[];

  // Filter state
  filters: NewCalendarFilters;

  // UI state
  showFilters: boolean;
  isLoading: boolean;

  // Multi-service expand/collapse state (only for list view)
  multiServiceExpanded: boolean;

  // Actions
  setViewMode: (viewMode: NewCalendarViewMode) => void;
  setCurrentDate: (date: DateTime) => void;

  // Multi-selection actions
  setSelectedAppointmentIds: (ids: string[]) => void;
  toggleAppointmentSelection: (id: string) => void;
  clearSelection: () => void;

  // Filter actions
  setStatusFilters: (filters: string[]) => void;
  setWorkerFilters: (filters: string[]) => void;
  setDateFilter: (filter: string) => void;
  clearAllFilters: () => void;

  // UI actions
  setShowFilters: (show: boolean) => void;
  setIsLoading: (loading: boolean) => void;

  // Multi-service expansion actions
  toggleMultiServiceExpansion: () => void;

  // Utility functions
  getDatePickerMode: () => DatePickerMode;
  isViewModeAvailableOnMobile: (mode: NewCalendarViewMode) => boolean;
}

const defaultFilters: NewCalendarFilters = {
  statusFilters: [],
  workerFilters: [],
  dateFilter: undefined,
};

export const useNewCalendarUIStore = create<NewCalendarUIState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        viewMode: "list",
        currentDate: DateTime.now(),
        selectedAppointmentIds: [],
        filters: defaultFilters,
        showFilters: false,
        isLoading: false,
        multiServiceExpanded: false,

        // View mode actions
        setViewMode: (viewMode) => {
          set({ viewMode });
        },

        setCurrentDate: (date) => {
          // Ensure we never store an invalid date
          const validDate = date?.isValid ? date : DateTime.now();
          set({ currentDate: validDate });
        },

        // Multi-selection actions
        setSelectedAppointmentIds: (ids) => set({ selectedAppointmentIds: ids }),

        toggleAppointmentSelection: (id) =>
          set((state) => ({
            selectedAppointmentIds: state.selectedAppointmentIds.includes(id)
              ? state.selectedAppointmentIds.filter((appointmentId) => appointmentId !== id)
              : [...state.selectedAppointmentIds, id],
          })),

        clearSelection: () => set({ selectedAppointmentIds: [] }),

        // Filter actions
        setStatusFilters: (statusFilters) =>
          set((state) => ({
            filters: { ...state.filters, statusFilters },
          })),

        setWorkerFilters: (workerFilters) =>
          set((state) => ({
            filters: { ...state.filters, workerFilters },
          })),

        setDateFilter: (dateFilter) =>
          set((state) => ({
            filters: { ...state.filters, dateFilter },
          })),

        clearAllFilters: () =>
          set({
            filters: defaultFilters,
            selectedAppointmentIds: [],
          }),

        // UI actions
        setShowFilters: (show) => set({ showFilters: show }),
        setIsLoading: (loading) => set({ isLoading: loading }),

        // Multi-service expansion actions
        toggleMultiServiceExpansion: () =>
          set((state) => ({
            multiServiceExpanded: !state.multiServiceExpanded,
          })),

        // Utility functions
        getDatePickerMode: (): DatePickerMode => {
          const { viewMode } = get();
          switch (viewMode) {
            case "week":
              return "week";
            case "month":
              return "month";
            default:
              return "date";
          }
        },

        isViewModeAvailableOnMobile: (mode: NewCalendarViewMode): boolean => {
          // Week and month views are desktop only
          return mode !== "week" && mode !== "month";
        },
      }),
      {
        name: "new-calendar-ui-store",
        partialize: (state) => ({
          // Persist view preferences and filters
          viewMode: state.viewMode,
          filters: state.filters,
          showFilters: state.showFilters,
          multiServiceExpanded: state.multiServiceExpanded,
        }),
      }
    ),
    {
      name: "new-calendar-ui-store",
    }
  )
);

// Convenience hook for checking if filters are active
export const useHasActiveFilters = () => {
  const { filters, selectedAppointmentIds } = useNewCalendarUIStore();

  return (
    selectedAppointmentIds.length > 0 ||
    filters.statusFilters.length > 0 ||
    filters.workerFilters.length > 0 ||
    !!filters.dateFilter
  );
};

// Convenience hook for mobile view mode restrictions
export const useAvailableViewModes = () => {
  const { isViewModeAvailableOnMobile } = useNewCalendarUIStore();

  const allViewModes: NewCalendarViewMode[] = ["list", "daily", "worker", "3-day", "week", "month"];
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  return allViewModes.filter((mode) => !isMobile || isViewModeAvailableOnMobile(mode));
};
